/*
 *                     NOTICE
 *            COPYRIGHT 2024 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */
import { $createLinkNode, $isLinkNode } from "@lexical/link";
import { $isListNode, INSERT_ORDERED_LIST_COMMAND, INSERT_UNORDERED_LIST_COMMAND, ListNode } from "@lexical/list";
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import useLexicalEditable from '@lexical/react/useLexicalEditable';
import { $isHeadingNode } from "@lexical/rich-text";
import { $patchStyleText, $setBlocksType } from '@lexical/selection';
import { $findMatchingParent, $getNearestNodeOfType } from '@lexical/utils';
import * as AbReactResources from "abreact/generated/locale/AbReactResources";
import { AbPopup } from "abreact/incubator/ui/popup/AbPopup";
import { PopupPosition } from "abreact/incubator/ui/popup/PopupPosition";
import { PopupUtils } from "abreact/incubator/ui/popup/PopupUtils";
import { AbMarkdownImageDialog } from "abreact/input/markdown/AbMarkdownImageDialog";
import { AbMarkdownLexical } from "abreact/input/markdown/AbMarkdownLexical";
import { AbMarkdownLinkDialog } from "abreact/input/markdown/AbMarkdownLinkDialog";
import { $createAbLinkNode } from "abreact/input/markdown/nodes/AbLinkNode";
import { $isImageNode } from "abreact/input/markdown/nodes/ImageNode";
import { replaceLinkText } from "abreact/input/markdown/plugins/FloatingLinkEditorPlugin";
import { getSelectedNode } from "abreact/input/markdown/utils/getSelectedNode";
import { convertToMarkdownString } from "abreact/input/markdown/utils/MarkdownExport";
import { convertFromMarkdownString } from "abreact/input/markdown/utils/MarkdownImport";
import { AB_MARKDOWN_TRANSFORMERS } from "abreact/input/markdown/utils/MarkdownTransformers";
import { createImageNode, standard_protocols } from "abreact/input/markdown/utils/MarkdownUtils";
import { AbToolbar } from "abreact/layout/toolbar/AbToolbar";
import { AbButton } from "abreact/misc/AbButton";
import { AbColorPicker, isBlack, isWhite } from "abreact/misc/AbColorPicker";
import { AbToggleButton } from "abreact/misc/AbToggleButton";
import { AbDialog } from "abreact/popup/AbDialog";
import { URLUtils } from "abweb/utils/URLUtils";
import { $createParagraphNode, $createPoint, $createRangeSelection, $createTextNode, $getRoot, $getSelection, $getTextContent, $isNodeSelection, $isRangeSelection, $isRootOrShadowRoot, $setSelection, FORMAT_TEXT_COMMAND, SELECTION_CHANGE_COMMAND } from 'lexical';
import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
const LowPriority = 1;
const HighPriority = 3;

// Type of block to show, when we put a block-type dropdown on the toolbar.
const blockTypeToBlockName = {
  bullet: 'Bulleted List',
  check: 'Check List',
  code: 'Code Block',
  h1: 'Heading 1',
  h2: 'Heading 2',
  h3: 'Heading 3',
  h4: 'Heading 4',
  h5: 'Heading 5',
  h6: 'Heading 6',
  number: 'Numbered List',
  paragraph: 'Normal',
  quote: 'Quote'
};
const formatParagraph = editor => {
  editor.update(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      $setBlocksType(selection, () => $createParagraphNode());
    }
  });
};

// Show the markdown editor in its own dialog with OK/Cancel buttons
function AbMarkdownEditorPopout(props) {
  const [popoutVal, setPopoutVal] = React.useState(props.value);
  return /*#__PURE__*/React.createElement(AbDialog, {
    style: {
      height: "80vh",
      width: "80vw"
    },
    className: "markdown-editor-popout",
    title: AbReactResources.markdownEditor_popoutTitle(),
    closeButton: false,
    onOk: async () => {
      props.onOk(popoutVal);
      return true;
    },
    cancelButton: true,
    resizable: true
  }, /*#__PURE__*/React.createElement(AbMarkdownLexical, {
    value: popoutVal,
    onChange: x => setPopoutVal(x)
  }));
}
const MY_VERTICAL_FIXED_SIZE_POSITIONER = PopupUtils.createVerticalPositioner({
  spacing: 5
});

// Show the toolbar above the markdown editor.
// Updates when the selection changes.
export default function ToolbarPlugin(props) {
  const [editor] = useLexicalComposerContext();

  // state for showing the toolbar
  const [blockType, setBlockType] = useState('paragraph');
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [isStrikethrough, setIsStrikethrough] = useState(false);
  const [isCode, setIsCode] = useState(false);
  const [isLink, setIsLink] = useState(false);
  const [isImage, setIsImage] = useState(false);
  const [fontColor, setFontColor] = useState('black');
  const [bgColor, setBgColor] = useState('white');

  // state for editing links
  const [linkEnabled, setLinkEnabled] = useState(true);
  const [linkText, setLinkText] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const linkButtonRef = useRef(null);

  // state for editing images
  const [imageUrl, setImageUrl] = useState('');
  const [imageAlt, setImageAlt] = useState('');
  const [showImageDialog, setShowImageDialog] = useState(false);
  const imageButtonRef = useRef(null);

  // pull elements out of props so hooks are easier to write
  const {
    showText,
    setShowText,
    markdown,
    setMarkdown
  } = props;

  // set the link editing state variables based on the current selection
  const updateLinkState = useCallback(node => {
    if (node == null) return;
    const parent = node.getParent();
    if ($isLinkNode(parent)) {
      setLinkEnabled(true);
      setIsLink(true);
      setLinkUrl(parent.getURL());
      setLinkText(parent.getTextContent());
    } else if ($isLinkNode(node)) {
      setLinkEnabled(true);
      setIsLink(true);
      setLinkUrl(node.getURL());
      setLinkText(node.getTextContent());
    } else if (blockType === "code") {
      setLinkEnabled(false);
      setIsLink(false);
      setLinkUrl("");
      setLinkText("");
    } else {
      setLinkEnabled(node.getType() === "text" || node.getType() === "root" || node.getType() === "paragraph");
      setIsLink(false);
      setLinkUrl("");
      setLinkText($getTextContent());
    }
    if ($isImageNode(parent)) {
      setIsImage(true);
      setImageUrl(parent.getSrc());
      setImageAlt(parent.getAltText());
    } else if ($isImageNode(node)) {
      setIsImage(true);
      setImageUrl(node.getSrc());
      setImageAlt(node.getAltText());
    } else {
      setIsImage(false);
      setImageUrl("");
      setImageAlt("");
    }
  }, [blockType]);

  // update state of the buttons on the toolbar
  const $updateToolbar = useCallback(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      // Update text format
      setIsBold(selection.hasFormat('bold'));
      setIsItalic(selection.hasFormat('italic'));
      setIsUnderline(selection.hasFormat('underline'));
      setIsStrikethrough(selection.hasFormat('strikethrough'));
      setIsCode(selection.hasFormat('code'));

      // Update links
      const node = getSelectedNode(selection);
      updateLinkState(node);
      const anchorNode = selection.anchor.getNode();
      let element = anchorNode.getKey() === 'root' ? anchorNode : $findMatchingParent(anchorNode, e => {
        const parent = e.getParent();
        return parent !== null && $isRootOrShadowRoot(parent);
      });
      if (element === null) {
        element = anchorNode.getTopLevelElementOrThrow();
      }
      if ($isListNode(element)) {
        const parentList = $getNearestNodeOfType(anchorNode, ListNode);
        const type = parentList ? parentList.getListType() : element.getListType();
        setBlockType(type);
      } else {
        const type = $isHeadingNode(element) ? element.getTag() : element.getType();
        if (type in blockTypeToBlockName) {
          setBlockType(type);
        }
      }
    } else if ($isNodeSelection(selection)) {
      if (selection.getNodes().length === 1) {
        const node = selection.getNodes()[0];
        updateLinkState(node);
      }
    }
  }, [updateLinkState]);

  // helper to add or remove styles from the text
  const applyStyleText = useCallback((styles, skipHistoryStack) => {
    editor.update(() => {
      const selection = $getSelection();
      if (selection !== null) {
        $patchStyleText(selection, styles);
      }
    }, skipHistoryStack ? {
      tag: 'historic'
    } : {});
  }, [editor]);

  // color select button
  const onFontColorSelect = useCallback((value, skipHistoryStack) => {
    applyStyleText({
      color: isBlack(value) ? null : value
    }, skipHistoryStack);
    setFontColor(value);
  }, [applyStyleText]);

  // background color select button
  const onBgColorSelect = useCallback((value, skipHistoryStack) => {
    applyStyleText({
      'background-color': isWhite(value) ? null : value
    }, skipHistoryStack);
    setBgColor(value);
  }, [applyStyleText]);
  const createNewLink = useCallback((text, url) => {
    editor.update(() => {
      const sel = $getSelection();
      const linkNode = $createLinkNode(url);
      let protocol = URLUtils.getProtocol(url);
      if (protocol === undefined) {
        // can't parse? make it http
        protocol = "http";
        url = "http:" + url;
      }
      if (standard_protocols.has(protocol)) {
        const linkTextNode = $createTextNode(text);
        linkNode.append(linkTextNode);
      } else {
        const abLinkNode = $createAbLinkNode(text, url);
        linkNode.append(abLinkNode);
      }
      if ($isRangeSelection(sel)) {
        sel.insertNodes([linkNode]);
      } else {
        const root = $getRoot();
        root.append(linkNode);
      }
    });
    return Promise.resolve(true);
  }, [editor]);
  const updateCurrentLink = useCallback((text, url) => {
    replaceLinkText(editor, text, url);
    return Promise.resolve(true);
  }, [editor]);
  const createNewImage = useCallback((alt, url) => {
    editor.update(() => {
      const sel = $getSelection();
      if ($isRangeSelection(sel)) {
        const imageNode = createImageNode(alt, url);
        sel.insertNodes([imageNode]);
      }
    });
    return Promise.resolve(true);
  }, [editor]);
  const updateCurrentImage = useCallback((alt, url) => {
    editor.update(() => {
      const sel = $getSelection();
      let node = null;
      if ($isRangeSelection(sel)) {
        node = getSelectedNode(sel);
      } else if ($isNodeSelection(sel)) {
        node = sel.getNodes()[0];
      }
      if (node != null && $isImageNode(node)) {
        const newImage = createImageNode(alt, url);
        node.replace(newImage);
      }
    });
    return Promise.resolve(true);
  }, [editor]);
  const handleMarkdownToggle = useCallback(() => {
    editor.update(() => {
      const root = $getRoot();
      if (showText) {
        // was showing text, go to WYSIWYG
        setShowText(false);
        convertFromMarkdownString(markdown ?? "", AB_MARKDOWN_TRANSFORMERS);
      } else {
        // was showing WYSIWYG, go to text
        setShowText(true);
        const newMarkdown = convertToMarkdownString(AB_MARKDOWN_TRANSFORMERS);
        setMarkdown(newMarkdown);
      }
      root.selectEnd();
    });
  }, [editor, showText, setShowText, markdown, setMarkdown]);
  const showPopOut = useCallback(() => {
    PopupUtils.showPopupExplicitly(/*#__PURE__*/React.createElement(AbMarkdownEditorPopout, {
      value: markdown ?? "",
      onOk: newMarkdown => setMarkdown(newMarkdown)
    }));
  }, [markdown, setMarkdown]);
  useEffect(() => {
    return editor.registerUpdateListener(({
      editorState
    }) => {
      editorState.read(() => {
        $updateToolbar();
      });
    });
  }, [editor, $updateToolbar]);
  useEffect(() => {
    return editor.registerCommand(SELECTION_CHANGE_COMMAND, _payload => {
      $updateToolbar();
      return false;
    }, LowPriority);
  }, [editor, $updateToolbar]);
  useEffect(() => {
    // if we're in a codeblock, disable the format_text_commands
    return editor.registerCommand(FORMAT_TEXT_COMMAND, _payload => {
      return blockType === "code";
    }, HighPriority);
  }, [blockType, editor]);
  let editable = useLexicalEditable();
  if (blockType === "code") editable = false;

  // Normal toolbar element, used when floating toolbars are off.
  const toolbarElement = props.floating ? null : /*#__PURE__*/React.createElement(AbToolbar, {
    groups: [{
      items: [!props.disabledEditorTools?.bold && /*#__PURE__*/React.createElement(AbToggleButton, {
        className: "abui ab-no-border-button",
        tooltip: AbReactResources.markdownEditor_bold(),
        key: "bold",
        icon: "absdk:text-bold",
        value: isBold,
        disabled: !editable,
        onChange: () => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "bold")
      }), !props.disabledEditorTools?.italic && /*#__PURE__*/React.createElement(AbToggleButton, {
        className: "abui ab-no-border-button",
        tooltip: AbReactResources.markdownEditor_italic(),
        key: "italic",
        icon: "absdk:text-italic",
        value: isItalic,
        disabled: !editable,
        onChange: () => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "italic")
      }), !props.disabledEditorTools?.underline && /*#__PURE__*/React.createElement(AbToggleButton, {
        className: "abui ab-no-border-button",
        tooltip: AbReactResources.markdownEditor_underline(),
        key: "underline",
        icon: "absdk:text-underline",
        value: isUnderline,
        disabled: !editable,
        onChange: () => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "underline")
      }), !props.disabledEditorTools?.strikeThrough && /*#__PURE__*/React.createElement(AbToggleButton, {
        className: "abui ab-no-border-button",
        tooltip: AbReactResources.markdownEditor_strikethrough(),
        icon: "absdk:text-strikethrough",
        key: "strikethrough",
        value: isStrikethrough,
        disabled: !editable,
        onChange: () => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "strikethrough")
      }), !props.disabledEditorTools?.monospace && /*#__PURE__*/React.createElement(AbToggleButton, {
        className: "abui ab-no-border-button",
        tooltip: AbReactResources.markdownEditor_monospace(),
        icon: "absdk:text-monospace",
        key: "monospace",
        value: isCode,
        disabled: !editable,
        onChange: () => {
          editor.update(() => {
            const sel = $getSelection();
            if (!sel || sel.getTextContent().length === 0) {
              editor.dispatchCommand(FORMAT_TEXT_COMMAND, "code");
              return;
            }
            if ($isRangeSelection(sel)) {
              // remove tabs, newlines, multi-spaces
              const text = sel.getTextContent().replace(/[ \t\n]+/g, " ");
              const len = text.length;
              let offset = sel.isBackward() ? sel.focus.offset : sel.anchor.offset;
              // remove old text and insert new text, which removes the format
              sel.removeText();
              sel.insertText(text);
              // create a new selection so we can set "code" on it
              const newSel = $createRangeSelection();
              const nodes = sel.getNodes();
              if (sel.getTextContent() === text) offset = 0;
              const anchor = $createPoint(nodes[0].__key, offset, 'text');
              const focus = $createPoint(nodes[0].__key, offset + len, 'text');
              newSel.anchor = anchor;
              newSel.focus = focus;
              // now set "code"
              newSel.formatText("code");
              $setSelection(newSel);
            }
          });
        }
      })]
    }, {
      items: [!props.disabledEditorTools?.bulleted && /*#__PURE__*/React.createElement(AbToggleButton, {
        className: "abui ab-no-border-button",
        tooltip: AbReactResources.markdownEditor_unorderedList(),
        icon: "absdk:list",
        key: "unorderedList",
        value: blockType === "bullet",
        disabled: !editable,
        onChange: () => {
          if (blockType !== 'bullet') {
            editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
          } else {
            formatParagraph(editor);
          }
        }
      }), !props.disabledEditorTools?.numbered && /*#__PURE__*/React.createElement(AbToggleButton, {
        className: "abui ab-no-border-button",
        tooltip: AbReactResources.markdownEditor_orderedList(),
        icon: "absdk:list-numbered",
        key: "orderedlist",
        value: blockType === "number",
        disabled: !editable,
        onChange: () => {
          if (blockType !== 'number') {
            editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
          } else {
            formatParagraph(editor);
          }
        }
      })]
    }, {
      items: [!props.disabledEditorTools?.color && /*#__PURE__*/React.createElement(AbColorPicker, {
        icon: "absdk:text-color",
        tooltip: AbReactResources.markdownEditor_foregroundColorButton(),
        key: "color",
        value: fontColor,
        show12Colors: true,
        disabled: !editable,
        onChange: newColor => {
          onFontColorSelect(newColor, false);
        }
      }), !props.disabledEditorTools?.background && /*#__PURE__*/React.createElement(AbColorPicker, {
        icon: "absdk:text-highlight",
        tooltip: AbReactResources.markdownEditor_backgroundColorButton(),
        key: "backgroundColor",
        value: bgColor,
        show12Colors: true,
        disabled: !editable,
        onChange: newColor => {
          onBgColorSelect(newColor, false);
        }
      })]
    }, {
      items: [!props.disabledEditorTools?.link && /*#__PURE__*/React.createElement(AbToggleButton, {
        className: "abui ab-no-border-button",
        tooltip: AbReactResources.markdownEditor_createLink(),
        key: "link",
        abRef: linkButtonRef,
        value: isLink,
        disabled: !linkEnabled || !editable,
        onChange: () => setShowLinkDialog(true),
        icon: "absdk:linked-item"
      }), !props.disabledEditorTools?.image && /*#__PURE__*/React.createElement(AbToggleButton, {
        className: "abui ab-no-border-button",
        tooltip: AbReactResources.markdownEditor_createImage(),
        key: "image",
        abRef: imageButtonRef,
        value: isImage,
        disabled: !editable,
        onChange: () => setShowImageDialog(true),
        icon: "absdk:photo"
      })]
    }],
    fixedGroup: {
      items: [!props.disabledEditorTools?.textMode && /*#__PURE__*/React.createElement(AbToggleButton, {
        className: "abui ab-no-border-button",
        icon: "absdk:text-markdown",
        tooltip: AbReactResources.markdownEditor_editMarkdown(),
        key: "markdown",
        dataTestId: "edit-markdown-button",
        value: false // a new selected toolbar with this set true appears.
        ,
        onChange: handleMarkdownToggle
      })]
    }
  });

  // Floating toolbar element, really a popup containing a toolbar, used when floating toolbars are on.
  const floatingToolbarElement = !props.floating ? null : /*#__PURE__*/React.createElement(AbPopup, {
    className: "ab-markdown-editor-popup-toolbar",
    stayInInitialPosition: true,
    origin: props.origin.current ?? undefined,
    modal: false,
    onKeyDown: event => event.key === "Escape",
    onClose: () => {/* closed only on unmount */
    },
    closeOnBackgroundClick: false,
    positioner: MY_VERTICAL_FIXED_SIZE_POSITIONER /* PopupUtils.VERTICAL_FIXED_SIZE_POSITIONER */,
    position: PopupPosition.ABOVE,
    draggable: true,
    draggableElementSelector: ".ab-toolbar",
    flushContent: true
  }, /*#__PURE__*/React.createElement(AbToolbar, {
    groups: [{
      items: [!props.disabledEditorTools?.bold && /*#__PURE__*/React.createElement(AbToggleButton, {
        className: "abui ab-no-border-button",
        tooltip: AbReactResources.markdownEditor_bold(),
        key: "bold",
        icon: "absdk:text-bold",
        value: isBold,
        disabled: !editable,
        onChange: () => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "bold")
      }), !props.disabledEditorTools?.italic && /*#__PURE__*/React.createElement(AbToggleButton, {
        className: "abui ab-no-border-button",
        tooltip: AbReactResources.markdownEditor_italic(),
        key: "italic",
        icon: "absdk:text-italic",
        value: isItalic,
        disabled: !editable,
        onChange: () => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "italic")
      }), !props.disabledEditorTools?.underline && /*#__PURE__*/React.createElement(AbToggleButton, {
        className: "abui ab-no-border-button",
        tooltip: AbReactResources.markdownEditor_underline(),
        key: "underline",
        icon: "absdk:text-underline",
        value: isUnderline,
        disabled: !editable,
        onChange: () => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "underline")
      }), !props.disabledEditorTools?.strikeThrough && /*#__PURE__*/React.createElement(AbToggleButton, {
        className: "abui ab-no-border-button",
        tooltip: AbReactResources.markdownEditor_strikethrough(),
        icon: "absdk:text-strikethrough",
        key: "strikethrough",
        value: isStrikethrough,
        disabled: !editable,
        onChange: () => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "strikethrough")
      }), !props.disabledEditorTools?.monospace && /*#__PURE__*/React.createElement(AbToggleButton, {
        className: "abui ab-no-border-button",
        tooltip: AbReactResources.markdownEditor_monospace(),
        icon: "absdk:text-monospace",
        key: "monospace",
        value: isCode,
        disabled: !editable,
        onChange: () => editor.dispatchCommand(FORMAT_TEXT_COMMAND, "code")
      })]
    }, {
      items: [!props.disabledEditorTools?.popup && /*#__PURE__*/React.createElement(AbButton, {
        className: "abui ab-no-border-button",
        tooltip: AbReactResources.markdownEditor_popout(),
        icon: "absdk:pop-out",
        key: "popout",
        disabled: !editor.isEditable(),
        onClick: () => showPopOut()
      })]
    }]
  }));
  return /*#__PURE__*/React.createElement(React.Fragment, null, props.floating ? floatingToolbarElement : toolbarElement, showLinkDialog && /*#__PURE__*/React.createElement(AbMarkdownLinkDialog, {
    text: linkText,
    url: linkUrl,
    buttonEl: isLink ? null : linkButtonRef.current // how to communicate create/edit mode to dialog
    ,
    onOk: isLink ? updateCurrentLink : createNewLink,
    onClose: () => {
      setShowLinkDialog(false);
    }
  }), showImageDialog && /*#__PURE__*/React.createElement(AbMarkdownImageDialog, {
    alt: imageAlt,
    url: imageUrl,
    onOk: isImage ? updateCurrentImage : createNewImage,
    buttonEl: isImage ? null : imageButtonRef.current,
    onClose: () => setShowImageDialog(false)
  }));
}
//# sourceMappingURL=ToolbarPlugin.js.map
